import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useParticipant } from '../../contexts/ParticipantContext';
import { useAuth } from '../../contexts/AuthContext';
import { BrButton } from '@govbr-ds/react-components';
import { usePersonaAccessibility } from '../../hooks/usePersonaAccessibility';

export default function ParticipantLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { state, endSession } = useParticipant();
    const { user, signOut } = useAuth();

    // Apply accessibility features based on persona (zoom 200%, dark mode, high contrast)
    usePersonaAccessibility();

    const handleLogout = () => {
        endSession();
        signOut();
        navigate('/');
    };

    // Determine current step based on route
    const getStepFromPath = (path: string): number => {
        if (path.includes('onboarding') || path === '/participante') return 1;
        if (path.includes('instrucoes')) return 2;
        if (path.includes('tarefa')) return 3;
        if (path.includes('feedback')) return 4;
        if (path.includes('obrigado')) return 5;
        return 1;
    };

    const currentStep = getStepFromPath(location.pathname);
    const totalTasks = state.session?.assigned_tasks.length || 3;
    const currentTaskIndex = state.session?.current_task_index || 0;

    const steps = [
        { num: 1, label: 'Cadastro', icon: 'fa-clipboard-list' },
        { num: 2, label: 'Instruções', icon: 'fa-book-open' },
        { num: 3, label: 'Tarefas', icon: 'fa-check' },
        { num: 4, label: 'Avaliação', icon: 'fa-star' },
        { num: 5, label: 'Fim', icon: 'fa-flag-checkered' }
    ];

    return (
        <div className="min-h-screen bg-gray-2 d-flex flex-column">
            {/* Header Gov.br - Compact */}
            <header className="br-header compact">
                <div className="container-lg">
                    <div className="header-top">
                        <div className="header-logo">
                            <img src="/images/logotipo-lumen.svg" alt="Lumen Framework" height="30" />
                            <span className="br-divider vertical mx-half"></span>
                            <span className="text-weight-bold text-primary-default">Lumen Framework</span>
                        </div>
                        <div className="header-actions">
                            <div className="header-functions">
                                <div className="header-links d-flex align-items-center gap-3">
                                    {state.session && currentStep >= 3 && currentStep < 5 && (
                                        <div className="d-none d-sm-flex align-items-center gap-2">
                                            <span className="text-weight-semi-bold">
                                                Tarefa {currentTaskIndex + 1}/{totalTasks}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {user && (
                                <div className="header-login">
                                    <BrButton
                                        secondary
                                        onClick={handleLogout}
                                        aria-label="Sair"
                                    >
                                        Sair
                                    </BrButton>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="header-bottom">
                        <div className="header-menu">
                            <div className="header-info">
                                <div className="header-title">Pesquisa de Experiência</div>
                                <div className="header-subtitle">Participante</div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Progress Stepper */}
            {state.session && (
                <div className="bg-pure-0 shadow-sm-down py-3">
                    <div className="container-lg">
                        <div className="d-flex align-items-center justify-content-between">
                            {steps.map((step, idx) => (
                                <React.Fragment key={step.num}>
                                    <div className="d-flex flex-column align-items-center">
                                        <div
                                            className={`rounded-circle d-flex align-items-center justify-content-center text-sm text-weight-semi-bold
                                                ${currentStep > step.num
                                                    ? 'bg-success text-pure-0'
                                                    : currentStep === step.num
                                                        ? 'bg-primary-default text-pure-0'
                                                        : 'bg-gray-10 text-gray-60'
                                                }`}
                                            style={{ width: '40px', height: '40px' }}
                                        >
                                            {currentStep > step.num ? <i className="fas fa-check"></i> : <i className={`fas ${step.icon}`}></i>}
                                        </div>
                                        <span className={`text-xs mt-2 text-weight-medium d-none d-sm-block
                                            ${currentStep >= step.num ? 'text-primary-default' : 'text-gray-40'}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className={`flex-fill mx-2 rounded-pill
                                            ${currentStep > step.num ? 'bg-success' : 'bg-gray-10'}`}
                                            style={{ height: '4px' }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="flex-fill py-4">
                <div className="container-lg">
                    <Outlet />
                </div>
            </main>

            {/* Footer Gov.br */}
            <footer className="br-footer">
                <div className="container-lg">
                    <div className="row py-4 align-items-center">
                        <div className="col-12 col-md-6 text-center text-md-left mb-3 mb-md-0">
                            <p className="text-sm mb-1">Pesquisa realizada pela</p>
                            <p className="text-weight-semi-bold">Universidade de Brasília (UnB)</p>
                        </div>
                        <div className="col-12 col-md-6 text-center text-md-right">
                            <p className="text-sm mb-1">Dúvidas ou sugestões?</p>
                            <a href="mailto:contact@lumenframework.com" className="text-pure-0">
                                contact@lumenframework.com
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
